import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import type { ModuleId } from '@shared/chain';
import { useModules } from '@/config/AppModules';

export interface TabItem {
  to: string;
  label: string;
  icon: string;
  /** 「发现」Tab 本期推迟 → hidden = true（路由存在但不渲染入口） */
  hidden?: boolean;
  badge?: number;
  /** 模块开关映射：模块关闭时该 Tab 不渲染（me 常驻） */
  module?: ModuleId | 'always';
}

export const TAB_ITEMS: TabItem[] = [
  { to: '/chat', label: '聊天', icon: '💬', module: 'chat' },
  { to: '/feed', label: '广场', icon: '🌐', module: 'feed' },
  { to: '/discover', label: '发现', icon: '🧭', module: 'discover' },
  { to: '/me', label: '我的', icon: '👤', module: 'always' },
];

export interface TabBarProps {
  items?: TabItem[];
}

/**
 * 底部 Tab（<768px 单栏形态）。
 * 「发现」Tab 已开放（路由 `/discover` + 入口可见）；如需临时下线，给该项加 `hidden: true`。
 * 模块开关（超管理台）关闭时对应 Tab 隐藏（me 常驻）。
 */
export function TabBar({ items = TAB_ITEMS }: TabBarProps): JSX.Element {
  const { isOn } = useModules();
  const visible = items.filter((i) => !i.hidden && (i.module === 'always' || isOn((i.module ?? 'always') as ModuleId)));
  return (
    <nav className="safe-bottom flex h-14 shrink-0 items-stretch border-t border-line bg-panel md:hidden">
      {visible.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            clsx(
              'relative flex flex-1 flex-col items-center justify-center gap-0.5 text-xs',
              isActive ? 'text-accent' : 'text-dim',
            )
          }
        >
          <span aria-hidden className="text-base leading-none">
            {item.icon}
          </span>
          <span>{item.label}</span>
          {item.badge && item.badge > 0 ? (
            <span className="absolute right-1/2 top-1 translate-x-4 rounded-full bg-err px-1.5 text-[10px] text-white">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}

export default TabBar;
